import React, { useState, useEffect } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import PropTypes from 'prop-types';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { SpotlightTransition, Spotlight } from '@atlaskit/onboarding';

import { useCoordinationClient } from '@confluence/engagement-provider';

const i18n = defineMessages({
	watchButtonNudgeTitle: {
		id: 'watch-dialog.watch.nudge.title',
		defaultMessage: 'Watch for updates',
		description: 'Nudge spotlight title text which has an outline around the watch button.',
	},
	watchButtonNudgeContent: {
		id: 'watch-dialog.watch.nudge.content',
		defaultMessage: 'When content you’re watching is updated, you’ll receive a notification.',
		description: 'Nudge spotlight content text which has an outline around the watch button.',
	},
	watchButtonNudgeDismissText: {
		id: 'watch-dialog.watch.nudge.dismiss.action',
		defaultMessage: 'Dismiss',
		description: 'Button for user to dismiss the nudge for the watch button.',
	},
	watchButtonNudgeWatchText: {
		id: 'watch-dialog.watch.nudge.watch.action',
		defaultMessage: 'Watch',
		description: 'Button for user to watch the page from the nudge for the watch button.',
	},
});

const watchContentNudge = 'cc-watch-page-spotlight-nudge';

export const WatchContentNudgeSpotlight = ({
	onWatchFromNudgeButtonClick,
	onDismissNudgeButtonClick,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isActive, setIsActive] = useState(true);
	const [hasMessageStarted, setHasMessageStarted] = useState(false);

	const coordinationClient = useCoordinationClient();
	const onDismissNudge = () => {
		coordinationClient?.stop(watchContentNudge);
		setIsActive(false);
		onDismissNudgeButtonClick();
	};

	const onWatchFromNudgeClick = () => {
		coordinationClient?.stop(watchContentNudge);
		setIsActive(false);
		onWatchFromNudgeButtonClick();
	};

	useEffect(() => {
		const onShowNudge = () => {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'WatchDialogWatchContentNudge',
				},
			}).fire();
		};

		const checkNudgeStatus = async () => {
			const messageStarted = await coordinationClient.start(watchContentNudge);
			if (messageStarted) {
				setHasMessageStarted(messageStarted);
				onShowNudge();
			}
		};

		checkNudgeStatus();
	}, [isActive, coordinationClient, createAnalyticsEvent]);

	return (
		<SpotlightTransition>
			{isActive && hasMessageStarted && (
				<Spotlight
					testId="watch-content-nudge-spotlight"
					actions={[
						{
							text: intl.formatMessage(i18n.watchButtonNudgeWatchText),
							onClick: onWatchFromNudgeClick,
						},
						{
							text: intl.formatMessage(i18n.watchButtonNudgeDismissText),
							onClick: onDismissNudge,
						},
					]}
					dialogPlacement="bottom"
					heading={intl.formatMessage(i18n.watchButtonNudgeTitle)}
					target="content-watch-spotlight"
					key="content-watch-spotlight"
					dialogWidth={275}
					targetRadius={4}
				>
					<FormattedMessage {...i18n.watchButtonNudgeContent} />
				</Spotlight>
			)}
		</SpotlightTransition>
	);
};

WatchContentNudgeSpotlight.propTypes = {
	onWatchFromNudgeButtonClick: PropTypes.func,
	onDismissNudgeButtonClick: PropTypes.func,
};
